import { useTranslation } from '@silospot/i18n/react';
import { Check } from 'lucide-react';
import type { ReactElement } from 'react';

export interface ChecklistItem {
  label: string;
  done: boolean;
}

/**
 * Pinned progress banner (X/N + items + an action). Built for onboarding but
 * generic — reuse it for contextual nudges (e.g. connect Cloudflare/Telegram).
 */
export function SetupChecklist({
  items,
  actionLabel,
  onAction,
  title,
}: {
  items: ChecklistItem[];
  actionLabel: string;
  onAction: () => void;
  title?: string;
}): ReactElement {
  const { t } = useTranslation('pages');
  const done = items.filter((i) => i.done).length;
  return (
    <div className="rounded-xl border border-accent-border bg-gradient-to-br from-[#f0f5ff] to-surface p-4 shadow-[var(--shadow)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14.5px] font-bold text-text">{title ?? t('onb.bannerTitle')}</div>
          <div className="mt-0.5 text-[12.5px] text-text-2">
            {t('onb.progress', { done, total: items.length })}
          </div>
        </div>
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 rounded-[9px] bg-primary px-3.5 py-2 text-[13px] font-semibold text-white hover:opacity-90"
        >
          {actionLabel}
        </button>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-neutral-bg">
        <span
          className="block h-full rounded-full bg-primary transition-all"
          style={{ width: `${(done / Math.max(1, items.length)) * 100}%` }}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        {items.map((it, i) => (
          <div key={it.label} className="flex items-center gap-2 text-[13px]">
            <span
              className={`grid h-5 w-5 place-items-center rounded-full text-[10.5px] ${it.done ? 'bg-clean-fg text-white' : 'bg-neutral-bg text-text-3'}`}
            >
              {it.done ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            <span className={it.done ? 'text-text-3 line-through' : 'text-text'}>{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
